import type { TranslationKey } from "./translations";

type Translate = (
  key: TranslationKey,
  vars?: Record<string, string | number>,
) => string;

export interface CustomerErrorDetails {
  code: string;
  title: string;
  message: string;
}

interface ErrorPresentation {
  title: TranslationKey;
  message: TranslationKey;
}

interface CustomerErrorDetailsOptions {
  fallback?: TranslationKey | undefined;
  itemName?: string | undefined;
}

const promotionErrorMessages: Record<string, TranslationKey> = {
  promotion_code_invalid: "cart.promotionInvalid",
  promotion_code_expired: "cart.promotionExpired",
  promotion_code_ineligible: "cart.promotionIneligible",
  promotion_customer_limit_reached: "cart.promotionIneligible",
  promotion_first_order_unavailable: "cart.promotionIneligible",
  promotion_code_unavailable: "cart.promotionUnavailable",
  promotion_customer_unavailable: "cart.promotionUnavailable",
  promotion_unavailable: "cart.promotionUnavailable",
  promotion_usage_limit_reached: "cart.promotionUnavailable",
  promotion_attribution_unavailable: "cart.linkedOfferUnavailable",
};

const loyaltyErrorMessages: Record<string, TranslationKey> = {
  loyalty_promotion_conflict: "loyalty.promotionConflict",
  loyalty_insufficient_points: "loyalty.insufficientPoints",
  loyalty_customer_unavailable: "loyalty.customerUnavailable",
  loyalty_program_unavailable: "loyalty.programUnavailable",
  loyalty_no_eligible_spend: "loyalty.noEligibleSpend",
  loyalty_points_exceed_order: "loyalty.pointsExceedOrder",
  loyalty_points_below_minimum: "loyalty.pointsBelowMinimum",
};

const safeErrorMessages: Record<string, TranslationKey> = {
  minimum_order_not_met: "errors.minimumOrder",
  ordering_paused: "errors.orderingPaused",
  tenant_inactive: "errors.storefrontUnavailable",
  subscription_inactive: "errors.storefrontUnavailable",
  order_type_disabled: "errors.orderTypeUnavailable",
  asap_disabled: "errors.asapUnavailable",
  scheduled_orders_disabled: "errors.scheduledUnavailable",
  branch_unavailable: "errors.branchUnavailable",
  item_unavailable: "errors.itemUnavailable",
  variant_unavailable: "errors.variantUnavailable",
  addon_unavailable: "errors.addonUnavailable",
  addon_selection_invalid: "errors.addonUnavailable",
  payment_method_disabled: "errors.paymentUnavailable",
  delivery_disabled: "errors.deliveryDisabled",
  outside_delivery_zone: "errors.deliveryUnavailable",
  out_of_delivery_zone: "errors.deliveryUnavailable",
  customer_unavailable: "errors.customerUnavailable",
  quote_expired: "errors.quoteExpired",
  request_in_progress: "errors.requestInProgress",
  invalid_fulfillment_time: "errors.fulfillmentTime",
  delivery_address_required: "errors.deliveryAddress",
  location_required: "errors.deliveryAddress",
  invalid_payment_method: "errors.paymentUnavailable",
  variant_required: "errors.variantUnavailable",
  variant_not_allowed: "errors.variantUnavailable",
  customer_phone_required: "errors.customerDetails",
  invalid_phone: "orderType.invalidPhone",
  customer_inactive: "errors.customerUnavailable",
  otp_attempts_exhausted: "login.attemptsExhausted",
  registration_invalid: "login.registrationExpired",
  otp_resend_cooldown: "login.resendCooldown",
  otp_rate_limited: "login.rateLimited",
  sms_not_entitled: "login.smsUnavailable",
  sms_country_unsupported: "login.smsUnavailable",
  sms_provider_unavailable: "login.smsUnavailable",
  sms_connection_unavailable: "login.smsUnavailable",
  sms_credits_insufficient: "login.smsUnavailable",
  ...loyaltyErrorMessages,
};

const errorPresentations: Record<string, ErrorPresentation> = {
  minimum_order_not_met: {
    title: "errors.minimumOrderTitle",
    message: "errors.minimumOrder",
  },
  payment_method_disabled: {
    title: "errors.paymentUnavailableTitle",
    message: "errors.paymentUnavailable",
  },
  ordering_paused: {
    title: "errors.orderingPausedTitle",
    message: "errors.orderingPaused",
  },
  tenant_inactive: {
    title: "errors.storefrontUnavailableTitle",
    message: "errors.storefrontUnavailable",
  },
  subscription_inactive: {
    title: "errors.storefrontUnavailableTitle",
    message: "errors.storefrontUnavailable",
  },
  order_type_disabled: {
    title: "errors.orderTypeUnavailableTitle",
    message: "errors.orderTypeUnavailable",
  },
  asap_disabled: {
    title: "errors.orderTypeUnavailableTitle",
    message: "errors.asapUnavailable",
  },
  scheduled_orders_disabled: {
    title: "errors.orderTypeUnavailableTitle",
    message: "errors.scheduledUnavailable",
  },
  branch_unavailable: {
    title: "errors.branchUnavailableTitle",
    message: "errors.branchUnavailable",
  },
  item_unavailable: {
    title: "errors.itemUnavailableTitle",
    message: "errors.itemUnavailable",
  },
  variant_unavailable: {
    title: "errors.variantUnavailableTitle",
    message: "errors.variantUnavailable",
  },
  addon_unavailable: {
    title: "errors.addonUnavailableTitle",
    message: "errors.addonUnavailable",
  },
  addon_selection_invalid: {
    title: "errors.addonUnavailableTitle",
    message: "errors.addonUnavailable",
  },
  delivery_disabled: {
    title: "errors.deliveryUnavailableTitle",
    message: "errors.deliveryDisabled",
  },
  outside_delivery_zone: {
    title: "errors.deliveryUnavailableTitle",
    message: "errors.deliveryUnavailable",
  },
  out_of_delivery_zone: {
    title: "errors.deliveryUnavailableTitle",
    message: "errors.deliveryUnavailable",
  },
  customer_unavailable: {
    title: "errors.customerUnavailableTitle",
    message: "errors.customerUnavailable",
  },
  quote_expired: {
    title: "errors.quoteExpiredTitle",
    message: "errors.quoteExpired",
  },
  request_in_progress: {
    title: "errors.requestInProgressTitle",
    message: "errors.requestInProgress",
  },
};

const nonBlockingCartQuoteErrors = new Set([
  "minimum_order_not_met",
  ...Object.keys(promotionErrorMessages),
  ...Object.keys(loyaltyErrorMessages),
]);

const interruptionErrorCodes = new Set([
  "ordering_paused",
  "tenant_inactive",
  "subscription_inactive",
  "order_type_disabled",
  "asap_disabled",
  "scheduled_orders_disabled",
  "branch_unavailable",
  "item_unavailable",
  "variant_unavailable",
  "addon_unavailable",
  "addon_selection_invalid",
  "payment_method_disabled",
  "delivery_disabled",
  "outside_delivery_zone",
  "out_of_delivery_zone",
  "customer_unavailable",
  "quote_expired",
  "request_in_progress",
]);

export function customerErrorDetails(
  error: unknown,
  t: Translate,
  options: CustomerErrorDetailsOptions = {},
): CustomerErrorDetails {
  const code = customerErrorCode(error);
  if (code === "item_unavailable" && options.itemName) {
    return {
      code,
      title: t("errors.itemUnavailableNamedTitle", { item: options.itemName }),
      message: t("errors.itemUnavailableNamed", { item: options.itemName }),
    };
  }
  const promotionMessage = promotionErrorMessages[code];
  if (promotionMessage) {
    return {
      code,
      title: t("errors.promotionTitle"),
      message: t(promotionMessage),
    };
  }
  const presentation = errorPresentations[code];
  if (presentation) {
    return {
      code,
      title: t(presentation.title),
      message: t(presentation.message),
    };
  }
  const loyaltyMessage = loyaltyErrorMessages[code];
  if (loyaltyMessage) {
    return {
      code,
      title: t("loyalty.errorTitle"),
      message: t(loyaltyMessage),
    };
  }
  return {
    code,
    title: t("common.error"),
    message: t(options.fallback ?? "common.errorHint"),
  };
}

export function customerErrorMessage(
  error: unknown,
  t: Translate,
  fallback: TranslationKey = "common.error",
): string {
  const promotionMessage = promotionErrorMessages[customerErrorCode(error)];
  if (promotionMessage) return t(promotionMessage);
  const loyaltyMessage = loyaltyErrorMessages[customerErrorCode(error)];
  if (loyaltyMessage) return t(loyaltyMessage);
  const safeMessage = safeErrorMessages[customerErrorCode(error)];
  return safeMessage ? t(safeMessage) : t(fallback);
}

export function cartCanCommitAfterQuoteError(error: unknown): boolean {
  return nonBlockingCartQuoteErrors.has(customerErrorCode(error));
}

/** Operational conflicts interrupt the current customer journey and warrant a dialog. */
export function customerErrorUsesDialog(error: unknown): boolean {
  return interruptionErrorCodes.has(customerErrorCode(error));
}

/** Deterministic client/domain responses do not improve when repeated automatically. */
export function shouldRetryStorefrontQuery(
  failureCount: number,
  error: unknown,
): boolean {
  const status = customerErrorStatus(error);
  if (status !== null && status >= 400 && status < 500) return false;
  return failureCount < 2;
}

export type AvailabilityRefreshTarget =
  | "storefront-bootstrap"
  | "branches"
  | "compact-menu"
  | "menu-item"
  | "delivery-areas"
  | "delivery-check";

export function customerAvailabilityRefreshTargets(
  error: unknown,
): AvailabilityRefreshTarget[] {
  switch (customerErrorCode(error)) {
    case "ordering_paused":
    case "order_type_disabled":
    case "asap_disabled":
    case "scheduled_orders_disabled":
    case "tenant_inactive":
    case "subscription_inactive":
      return ["storefront-bootstrap"];
    case "branch_unavailable":
      return ["branches", "compact-menu", "menu-item"];
    case "item_unavailable":
    case "variant_unavailable":
    case "addon_unavailable":
    case "addon_selection_invalid":
      return ["compact-menu", "menu-item"];
    case "delivery_disabled":
    case "outside_delivery_zone":
    case "out_of_delivery_zone":
      return ["delivery-areas", "delivery-check"];
    default:
      return [];
  }
}

export function customerErrorCode(error: unknown): string {
  if (!error || typeof error !== "object" || !("code" in error)) return "";
  return typeof error.code === "string" ? error.code : "";
}

export function customerErrorItemId(error: unknown): string {
  if (!error || typeof error !== "object" || !("details" in error)) return "";
  const details = error.details;
  if (!details || typeof details !== "object" || !("itemId" in details))
    return "";
  return typeof details.itemId === "string" ? details.itemId : "";
}

function customerErrorStatus(error: unknown): number | null {
  if (!error || typeof error !== "object" || !("status" in error)) return null;
  return typeof error.status === "number" ? error.status : null;
}
